/* eslint-disable @typescript-eslint/no-explicit-any */
// Innobo Hub UI kit v1. Las pantallas nuevas (y las que vayamos migrando) usan esto en vez de reactstrap.
// El estilo vive en ihub.scss bajo .ihub: las pantallas viejas no cambian.
// Empezó con Notifications; la idea es pasar lista por lista.
import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Select from 'react-select';
import moment from 'moment';
// @ts-ignore: react-flatpickr no trae tipos en esta versión
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import './ihub.scss';

// Inter, una sola vez, sin depender del orden del CSS del build.
if (typeof document !== 'undefined' && !document.getElementById('ihub-font')) {
  const l = document.createElement('link');
  l.id = 'ihub-font';
  l.rel = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
  document.head.appendChild(l);
}

const cx = (...c: any[]) => c.filter(Boolean).join(' ');
const F = 'YYYY-MM-DD';

/* ---------- Página ---------- */
export const Page = ({ eyebrow, title, subtitle, actions, children }: any) => (
  <div className="page-content ihub">
    <div className="ihub-shell">
      <header className="ihub-head">
        <div>
          {eyebrow ? <div className="ihub-eyebrow">{eyebrow}</div> : null}
          <h1 className="ihub-title">{title}</h1>
          {subtitle ? <p className="ihub-subtitle">{subtitle}</p> : null}
        </div>
        {actions ? <div className="ihub-actions">{actions}</div> : null}
      </header>
      {children}
    </div>
  </div>
);

export const Card = ({ accent, flush, className, children }: any) => (
  <section className={cx('ihub-card', accent && 'ihub-card--accent', flush && 'ihub-card--flush', className)}>{children}</section>
);

/* ---------- Botones y badges ---------- */
export const Button = ({ variant = 'primary', size, icon, children, className, type = 'button', ...rest }: any) => (
  <button
    type={type}
    className={cx('ihub-btn', `ihub-btn--${variant}`, size && `ihub-btn--${size}`, !children && 'ihub-btn--icon', className)}
    {...rest}
  >
    {icon ? <i className={cx('bx', icon)} /> : null}
    {children ? <span>{children}</span> : null}
  </button>
);

export const Badge = ({ tone = 'neutral', dot, children }: any) => (
  <span className={cx('ihub-badge', `ihub-badge--${tone}`)}>
    {dot ? <span className="ihub-badge__dot" /> : null}
    {children}
  </span>
);

export const Alert = ({ tone = 'info', title, children }: any) => (
  <div className={cx('ihub-alert', `ihub-alert--${tone}`)} role="alert">
    {title ? <div className="ihub-alert__title">{title}</div> : null}
    <div className="ihub-alert__body">{children}</div>
  </div>
);

export const EmptyState = ({ icon = 'bx-search-alt', title = 'Nothing here yet', text }: any) => (
  <div className="ihub-empty">
    <span className="ihub-empty__icon">
      <i className={cx('bx', icon)} />
    </span>
    <div className="ihub-empty__title">{title}</div>
    {text ? <p className="ihub-empty__text">{text}</p> : null}
  </div>
);

/* ---------- Métricas ---------- */
export const Stat = ({ label, value, tone = 'neutral', icon, active, onClick, loading }: any) => (
  <button type="button" className={cx('ihub-stat', `ihub-stat--${tone}`, active && 'is-on')} onClick={onClick} disabled={!onClick}>
    <span className="ihub-stat__label">
      {icon ? <i className={cx('bx', icon)} /> : null}
      {label}
    </span>
    <span className="ihub-stat__value">
      {loading ? <span className="ihub-skel" style={{ display: 'inline-block', width: 64, height: 26 }} /> : Number(value || 0).toLocaleString()}
    </span>
  </button>
);

/* ---------- Campos ---------- */
export const Field = ({ label, hint, className, children }: any) => (
  <div className={cx('ihub-field', className)}>
    {label ? <label className="ihub-label">{label}</label> : null}
    {children}
    {hint ? <span className="ihub-hint">{hint}</span> : null}
  </div>
);

export const TextInput = ({ icon, className, ...rest }: any) =>
  icon ? (
    <div className="ihub-inputwrap">
      <i className={cx('bx', icon)} />
      <input className={cx('ihub-input', className)} {...rest} />
    </div>
  ) : (
    <input className={cx('ihub-input', className)} {...rest} />
  );

export const Segmented = ({ options, value, onChange, size }: any) => (
  <div className={cx('ihub-seg', size && `ihub-seg--${size}`)} role="radiogroup">
    {options.map((o: any) => (
      <button
        key={String(o.value)}
        type="button"
        role="radio"
        aria-checked={o.value === value}
        className={cx(o.value === value && 'is-on')}
        onClick={() => onChange(o.value)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// react-select con el mismo look de los inputs. El menú va a <body> para que ninguna tarjeta lo corte.
const selectStyles: any = {
  control: (b: any, s: any) => ({
    ...b,
    minHeight: 44,
    borderRadius: 10,
    borderColor: s.isFocused ? '#140FBA' : '#DADAF0',
    background: s.isFocused ? '#FFF' : '#FCFCFF',
    boxShadow: s.isFocused ? '0 0 0 4px rgba(20,15,186,.12)' : 'none',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { borderColor: s.isFocused ? '#140FBA' : '#C4C4E6' },
  }),
  valueContainer: (b: any) => ({ ...b, padding: '0 12px' }),
  placeholder: (b: any) => ({ ...b, color: '#8B8BB3', fontWeight: 500 }),
  singleValue: (b: any) => ({ ...b, color: '#1A1A5B' }),
  input: (b: any) => ({ ...b, color: '#1A1A5B' }),
  indicatorSeparator: () => ({ display: 'none' }),
  clearIndicator: (b: any) => ({ ...b, color: '#8B8BB3', padding: 6, ':hover': { color: '#E02401' } }),
  dropdownIndicator: (b: any, s: any) => ({
    ...b,
    color: '#4E4E83',
    transition: 'transform .15s',
    transform: s.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
  }),
  menuPortal: (b: any) => ({ ...b, zIndex: 1060 }),
  menu: (b: any) => ({
    ...b,
    marginTop: 6,
    borderRadius: 12,
    border: '1px solid #DADAF0',
    boxShadow: '0 18px 50px rgba(26,26,91,.16)',
    overflow: 'hidden',
    fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', Arial, sans-serif",
  }),
  menuList: (b: any) => ({ ...b, padding: 6 }),
  option: (b: any, s: any) => ({
    ...b,
    borderRadius: 8,
    fontSize: 13.5,
    fontWeight: s.isSelected ? 700 : 500,
    color: s.isSelected ? '#FFF' : '#1A1A5B',
    background: s.isSelected ? '#140FBA' : s.isFocused ? '#EEF0FF' : 'transparent',
    cursor: 'pointer',
    ':active': { background: s.isSelected ? '#140FBA' : '#DCDFFF' },
  }),
  noOptionsMessage: (b: any) => ({ ...b, fontSize: 13, color: '#4E4E83' }),
};

export const SelectField = ({ options = [], value, onChange, placeholder = 'All', isClearable = true, isSearchable = true }: any) => {
  const actual = options.find((o: any) => String(o.value) === String(value)) || null;
  return (
    <Select
      options={options}
      value={actual}
      onChange={(o: any) => onChange(o ? o.value : '')}
      placeholder={placeholder}
      isClearable={isClearable}
      isSearchable={isSearchable}
      styles={selectStyles}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
    />
  );
};

/* ---------- Rango de fechas (flatpickr, el mismo motor del resto del Hub, con el tema nuevo) ---------- */
const PRESETS = [
  { key: 'today', label: 'Today', range: () => [moment(), moment()] },
  { key: '7d', label: 'Last 7 days', range: () => [moment().subtract(6, 'days'), moment()] },
  { key: '30d', label: 'Last 30 days', range: () => [moment().subtract(29, 'days'), moment()] },
  { key: 'month', label: 'This month', range: () => [moment().startOf('month'), moment()] },
];

// Constante fuera del componente: react-flatpickr re-aplica las opciones que cambian de referencia.
const FP_OPCIONES = {
  mode: 'range',
  dateFormat: 'Y-m-d',
  altInput: true,
  altFormat: 'M j, Y',
  altInputClass: 'ihub-input ihub-input--date',
  locale: { rangeSeparator: '  →  ', firstDayOfWeek: 1 },
};

export const DateRange = ({ from, to, onChange, presets = true }: any) => {
  // Mismo array mientras no cambien las fechas: si no, flatpickr resetea el rango a mitad de selección.
  const valor = useMemo(() => [from, to].filter(Boolean), [from, to]);
  const activo = PRESETS.find((p) => {
    const [a, b] = p.range();
    return a.format(F) === from && b.format(F) === to;
  });
  return (
    <div>
      <Flatpickr
        value={valor}
        options={FP_OPCIONES}
        onReady={(_d: any, _s: any, fp: any) => fp.calendarContainer.classList.add('ihub-fp')}
        onClose={(dates: any[], _s: any, fp: any) => {
          if (dates && dates.length === 2) onChange(moment(dates[0]).format(F), moment(dates[1]).format(F));
          else fp.setDate(valor, false);
        }}
      />
      {presets ? (
        <div className="ihub-chips">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={cx('ihub-chip', activo && activo.key === p.key && 'is-on')}
              onClick={() => {
                const [a, b] = p.range();
                onChange(a.format(F), b.format(F));
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

/* ---------- Tabla ---------- */
// columns: [{ key, label, width?, sortable?, sortKey?, render?(row) }]
// sort: { key, dir: 'ASC' | 'DESC' }
export const DataTable = ({
  columns,
  rows = [],
  rowKey = 'id',
  total = 0,
  page = 1,
  pageSize = 50,
  pageSizes = [25, 50, 100],
  onPage,
  onPageSize,
  sort,
  onSort,
  onRowClick,
  loading,
  empty,
}: any) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const desde = total ? (page - 1) * pageSize + 1 : 0;
  const hasta = Math.min(total, page * pageSize);
  const primeraCarga = loading && !rows.length;

  const ordenar = (c: any) => {
    if (!c.sortable || !onSort) return;
    const key = c.sortKey || c.key;
    onSort({ key, dir: sort && sort.key === key && sort.dir === 'DESC' ? 'ASC' : 'DESC' });
  };

  return (
    <div className={cx('ihub-table-card', loading && 'is-loading')}>
      {loading ? <div className="ihub-loadbar" /> : null}
      <div className="ihub-table-wrap">
        <table className="ihub-table">
          <thead>
            <tr>
              {columns.map((c: any) => {
                const key = c.sortKey || c.key;
                const on = sort && sort.key === key;
                return (
                  <th
                    key={c.key}
                    style={c.width ? { width: c.width } : undefined}
                    className={cx(c.sortable && 'is-sortable', on && 'is-sorted')}
                    onClick={() => ordenar(c)}
                  >
                    {c.label}
                    {c.sortable ? (
                      <i className={cx('bx ihub-sort', on ? (sort.dir === 'ASC' ? 'bx-chevron-up' : 'bx-chevron-down') : 'bx-sort-alt-2')} />
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {primeraCarga ? (
              Array.from({ length: 8 }).map((_x, i) => (
                <tr key={`sk-${i}`}>
                  {columns.map((c: any, j: number) => (
                    <td key={c.key}>
                      <div className="ihub-skel" style={{ width: `${45 + ((i * 37 + j * 23) % 50)}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length ? (
              rows.map((r: any) => (
                <tr
                  key={r[rowKey]}
                  className={cx(onRowClick && 'is-clickable')}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={() => onRowClick && onRowClick(r)}
                  onKeyDown={(e: any) => onRowClick && e.key === 'Enter' && onRowClick(r)}
                >
                  {columns.map((c: any) => (
                    <td key={c.key}>{c.render ? c.render(r) : r[c.key]}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="ihub-td-empty">
                  {empty || <EmptyState />}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="ihub-table-foot">
        <span>
          {total ? (
            <>
              Showing <b>{desde.toLocaleString()}</b> to <b>{hasta.toLocaleString()}</b> of <b>{total.toLocaleString()}</b>
            </>
          ) : (
            'No results'
          )}
        </span>
        <div className="ihub-pager">
          {onPageSize ? (
            <Segmented size="sm" options={pageSizes.map((n: number) => ({ value: n, label: String(n) }))} value={pageSize} onChange={onPageSize} />
          ) : null}
          <Button variant="secondary" size="sm" icon="bx-chevron-left" aria-label="Previous page" disabled={page <= 1} onClick={() => onPage && onPage(page - 1)} />
          <span className="ihub-pager__n">
            Page {page} of {pages}
          </span>
          <Button variant="secondary" size="sm" icon="bx-chevron-right" aria-label="Next page" disabled={page >= pages} onClick={() => onPage && onPage(page + 1)} />
        </div>
      </div>
    </div>
  );
};

/* ---------- Drawer lateral (reemplaza los popups para ver detalle) ---------- */
export const Drawer = ({ open, onClose, eyebrow, title, badges, actions, children }: any) => {
  useEffect(() => {
    if (!open) return undefined;
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', tecla);
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', tecla);
      document.body.style.overflow = antes;
    };
  }, [open, onClose]);

  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="ihub-portal">
      <div className="ihub-overlay" onClick={onClose} />
      <aside className="ihub-drawer" role="dialog" aria-modal="true">
        <header className="ihub-drawer__head">
          <div className="ihub-drawer__titles">
            {eyebrow ? <div className="ihub-eyebrow">{eyebrow}</div> : null}
            <h2 className="ihub-drawer__title">{title}</h2>
            {badges ? <div className="ihub-drawer__badges">{badges}</div> : null}
          </div>
          <div className="ihub-actions">
            {actions}
            <Button variant="ghost" icon="bx-x" aria-label="Close" onClick={onClose} />
          </div>
        </header>
        <div className="ihub-drawer__body">{children}</div>
      </aside>
    </div>,
    document.body,
  );
};

// items: [{ label, value, wide? }]
export const MetaGrid = ({ items }: any) => (
  <dl className="ihub-meta">
    {items.map((it: any) => (
      <div key={it.label} className={cx('ihub-meta__item', it.wide && 'is-wide')}>
        <dt>{it.label}</dt>
        <dd>{it.value}</dd>
      </div>
    ))}
  </dl>
);
